import { BUBBLE_EXPORT_FONT_FAMILY, BUBBLE_FONT_FAMILY, BUBBLE_LINE_HEIGHT, fitBubbleText, fitSpeakCaption, speakCaptionInnerSize, speakCaptionLayout } from "./bubble";
import { HEAD_GAP_LEFT_DEG, HEAD_GAP_RIGHT_DEG, HEAD_R, HEAD_RY, type EyePose, type Pose } from "./pose";
import { resolveBubbleFill, type CaptionOptions, type DwimTheme } from "./theme";

export type Primitive =
  | { type: "ellipse"; cx: number; cy: number; rx: number; ry: number; fill: string; stroke: string; strokeWidth: number }
  | { type: "path"; d: string; fill: string; stroke: string; strokeWidth: number }
  | { type: "rect"; x: number; y: number; width: number; height: number; rotation: number; fill: string; stroke: string; strokeWidth: number }
  | { type: "text"; x: number; y: number; text: string; fontSize: number; fill: string; anchor: "start" | "middle" | "end"; fontFamily?: string };

export function strokeForSize(pixelSize: number): number {
  return pixelSize < 120 ? 4 : 2.5;
}

export function dwimAriaLabel(bubble: Pose["bubble"]): string {
  const trimmed = bubble.text.trim();
  if (bubble.progress > 0 && trimmed) {
    return `The Dwim: ${trimmed}`;
  }
  return "The Dwim";
}

function rotateAround(
  px: number,
  py: number,
  cx: number,
  cy: number,
  deg: number,
): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

function facePoint(pose: Pose, lx: number, ly: number): { x: number; y: number } {
  return rotateAround(
    pose.x + lx * pose.scale,
    pose.y + ly * pose.scale,
    pose.x,
    pose.y,
    pose.rotation,
  );
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function strokePath(d: string, theme: DwimTheme, sw: number): Primitive {
  return { type: "path", d, fill: "none", stroke: theme.foreground, strokeWidth: sw };
}

function local(pose: Pose, lx: number, ly: number): { x: number; y: number } {
  return rotateAround(
    pose.x + lx * pose.scale,
    pose.y + ly * pose.scale,
    pose.x,
    pose.y,
    pose.rotation,
  );
}

/** Point on the head ellipse. 0° is the top; angles increase clockwise. */
function headPolar(pose: Pose, degFromTopCw: number): { x: number; y: number } {
  const rad = (degFromTopCw * Math.PI) / 180;
  return local(pose, HEAD_R * Math.sin(rad), -HEAD_RY * Math.cos(rad));
}

/** Semicircle flick, rotated CCW from horizontal so it stands nearly vertical. */
const FLICK_TILT_DEG = 80;
const FLICK_R_RATIO = 0.32;

/**
 * Open δ loop: clockwise elliptical arc, not closed (no Z).
 * Starts at the right lip of the top gap (where the flick attaches) and
 * arrives at the left lip (the open end).
 */
function headLoopPath(pose: Pose): string {
  const start = headPolar(pose, HEAD_GAP_RIGHT_DEG);
  const end = headPolar(pose, -HEAD_GAP_LEFT_DEG);
  const rx = HEAD_R * pose.scale;
  const ry = HEAD_RY * pose.scale;
  return `M ${start.x} ${start.y} A ${rx} ${ry} ${pose.rotation} 1 1 ${end.x} ${end.y}`;
}

/** δ loop + flick only — no tails, face, or papers. */
export function headOutlinePrimitives(
  pose: Pose,
  theme: DwimTheme,
  pixelSize: number,
): Primitive[] {
  const sw = strokeForSize(pixelSize);
  return [
    {
      type: "path",
      d: headLoopPath(pose),
      fill: sheetFill(theme),
      stroke: theme.foreground,
      strokeWidth: sw * 2,
    },
    {
      type: "path",
      d: headFlickPath(pose),
      fill: "none",
      stroke: theme.foreground,
      strokeWidth: sw * 2,
    },
  ];
}

/** Small ~semicircle from the right lip of the gap, standing up (~80° from horizontal). */
function headFlickPath(pose: Pose): string {
  const gapRad = (HEAD_GAP_RIGHT_DEG * Math.PI) / 180;
  const attachLx = HEAD_R * Math.sin(gapRad);
  const attachLy = -HEAD_RY * Math.cos(gapRad);
  const flickR = HEAD_R * FLICK_R_RATIO;
  const tilt = (FLICK_TILT_DEG * Math.PI) / 180;
  const endLx = attachLx + 2 * flickR * Math.cos(tilt);
  const endLy = attachLy - 2 * flickR * Math.sin(tilt);
  const start = local(pose, attachLx, attachLy);
  const end = local(pose, endLx, endLy);
  const fr = flickR * pose.scale;
  return `M ${start.x} ${start.y} A ${fr} ${fr} 0 0 1 ${end.x} ${end.y}`;
}

function archEyePath(pose: Pose, eye: EyePose): string {
  const half = 5 * pose.scale * Math.max(eye.openness, 0.35);
  const lift = 3.2 * pose.scale * Math.max(eye.openness, 0.35);
  const a = facePoint(pose, eye.x - half, eye.y + 1);
  const b = facePoint(pose, eye.x, eye.y - lift);
  const c = facePoint(pose, eye.x + half, eye.y + 1);
  return `M ${a.x} ${a.y} Q ${b.x} ${b.y} ${c.x} ${c.y}`;
}

function closedLidPath(pose: Pose, eye: EyePose): string {
  const half = 5.5 * pose.scale;
  const wave = pose.mouth.wave;
  if (wave > 0) {
    const steps = 12;
    const parts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const lx = eye.x - half + t * half * 2;
      const ly = eye.y + wave * pose.scale * Math.sin(t * Math.PI * 4);
      const p = facePoint(pose, lx, ly);
      parts.push(`${i === 0 ? "M" : "L"} ${p.x} ${p.y}`);
    }
    return parts.join(" ");
  }
  const a = facePoint(pose, eye.x - half, eye.y);
  const b = facePoint(pose, eye.x, eye.y + 3.2 * pose.scale);
  const c = facePoint(pose, eye.x + half, eye.y);
  return `M ${a.x} ${a.y} Q ${b.x} ${b.y} ${c.x} ${c.y}`;
}

function browPath(pose: Pose, eye: EyePose, outside: boolean): string {
  const span = (outside ? 5.2 : 4.2) * pose.scale * (0.55 + 0.45 * pose.brow);
  const lift = (outside ? 26 : 7 + 2.5 * pose.brow) * pose.scale;
  const bow = (outside ? 2.4 : 1.6 + pose.brow) * pose.scale;
  const a = facePoint(pose, eye.x - span, eye.y - lift + bow * 0.4);
  const b = facePoint(pose, eye.x, eye.y - lift - bow);
  const c = facePoint(pose, eye.x + span, eye.y - lift + bow * 0.4);
  return `M ${a.x} ${a.y} Q ${b.x} ${b.y} ${c.x} ${c.y}`;
}

function mouthPrimitives(pose: Pose, theme: DwimTheme, sw: number): Primitive[] {
  const { kind, width, curvature, wave, y: extraY, bias } = pose.mouth;
  if (kind === "none") return [];
  const hw = (width / 2) * pose.scale;
  const my = 10 + (HEAD_RY - 10) * 0.25 + extraY;
  if (kind === "o") {
    const c = facePoint(pose, 0, my);
    const rx = Math.max(2.4, hw * 0.55);
    const ry = rx * (width >= 14 ? 1.15 : 0.9);
    return [{ type: "ellipse", cx: c.x, cy: c.y, rx, ry, fill: "none", stroke: theme.foreground, strokeWidth: sw }];
  }
  if (kind === "line") {
    const a = facePoint(pose, -hw, my);
    const b = facePoint(pose, hw, my);
    return [strokePath(`M ${a.x} ${a.y} L ${b.x} ${b.y}`, theme, sw)];
  }
  if (kind === "wavy") {
    const steps = 14;
    const amp = wave * pose.scale;
    const parts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const p = facePoint(pose, -hw + t * hw * 2, my + amp * Math.sin(t * Math.PI * 4));
      parts.push(`${i === 0 ? "M" : "L"} ${p.x} ${p.y}`);
    }
    return [strokePath(parts.join(" "), theme, sw)];
  }
  const a = facePoint(pose, -hw, my - bias);
  const b = facePoint(pose, -hw * 0.12, my + curvature * pose.scale);
  const c = facePoint(pose, hw, my);
  return [strokePath(`M ${a.x} ${a.y} Q ${b.x} ${b.y} ${c.x} ${c.y}`, theme, sw)];
}

function isTransparentFill(color: string): boolean {
  return color === "transparent" || color === "none" || color === "";
}

function sheetFill(theme: DwimTheme): string {
  return isTransparentFill(theme.background) ? "#FFFFFF" : theme.background;
}

function scleraFill(theme: DwimTheme): string {
  return isTransparentFill(theme.background) ? "#FFFFFF" : theme.background;
}

function pupilFill(theme: DwimTheme): string {
  return isTransparentFill(theme.background) ? "#1A1A1A" : theme.foreground;
}

function goggleScleraPath(pose: Pose): string {
  const ox = (pose.eyeL.x + pose.eyeR.x) / 2;
  const oy = (pose.eyeL.y + pose.eyeR.y) / 2;
  const open = Math.max(0.2, (pose.eyeL.openness + pose.eyeR.openness) / 2);
  const s = 1.3;
  const left = { cx: -4.6 * s, cy: 0, rx: 5.4 * s, ry: 10.8 * s };
  const right = { cx: 5.3 * s, cy: -0.6 * s, rx: 6.4 * s, ry: 12.8 * s };
  const inside = (
    x: number,
    y: number,
    e: { cx: number; cy: number; rx: number; ry: number },
  ): boolean => {
    const dx = (x - e.cx) / e.rx;
    const dy = (y - e.cy) / (e.ry * open);
    return dx * dx + dy * dy < 0.97;
  };
  const samples: { x: number; y: number }[] = [];
  for (const e of [left, right]) {
    const other = e === left ? right : left;
    for (let i = 0; i < 48; i++) {
      const rad = (i / 48) * Math.PI * 2;
      const x = e.cx + e.rx * Math.sin(rad);
      const y = e.cy - e.ry * open * Math.cos(rad);
      if (!inside(x, y, other)) samples.push({ x, y });
    }
  }
  const mx = (left.cx + right.cx) / 2;
  const my = (left.cy + right.cy) / 2;
  samples.sort((a, b) => Math.atan2(a.y - my, a.x - mx) - Math.atan2(b.y - my, b.x - mx));
  const pts = samples.map((s) => facePoint(pose, ox + s.x, oy + s.y));
  return `M ${pts[0].x} ${pts[0].y} ${pts.slice(1).map((p) => `L ${p.x} ${p.y}`).join(" ")} Z`;
}

function pupilEllipsePath(pose: Pose, eye: EyePose, rx: number, ry: number): string {
  const lx = eye.x + eye.pupilX;
  const ly = eye.y + eye.pupilY;
  const a = facePoint(pose, lx + rx, ly);
  const b = facePoint(pose, lx - rx, ly);
  const wrx = rx * pose.scale;
  const wry = ry * pose.scale;
  return `M ${a.x} ${a.y} A ${wrx} ${wry} ${pose.rotation} 1 1 ${b.x} ${b.y} A ${wrx} ${wry} ${pose.rotation} 1 1 ${a.x} ${a.y} Z`;
}

function goggleEyePrimitives(pose: Pose, theme: DwimTheme, sw: number): Primitive[] {
  const open = (pose.eyeL.openness + pose.eyeR.openness) / 2;
  if (open <= 0.12) {
    return [
      strokePath(closedLidPath(pose, pose.eyeL), theme, sw),
      strokePath(closedLidPath(pose, pose.eyeR), theme, sw),
    ];
  }
  const whites = scleraFill(theme);
  const ink = pupilFill(theme);
  const prims: Primitive[] = [
    {
      type: "path",
      d: goggleScleraPath(pose),
      fill: whites,
      stroke: theme.foreground,
      strokeWidth: sw * 0.75,
    },
  ];
  const leftRy = 5.2 * Math.max(pose.eyeL.openness, 0.2);
  const rightRy = 6.0 * Math.max(pose.eyeR.openness, 0.2);
  prims.push({
    type: "path",
    d: pupilEllipsePath(pose, pose.eyeL, 2.5, leftRy),
    fill: ink,
    stroke: "none",
    strokeWidth: 0,
  });
  prims.push({
    type: "path",
    d: pupilEllipsePath(pose, pose.eyeR, 2.85, rightRy),
    fill: ink,
    stroke: "none",
    strokeWidth: 0,
  });
  if (open > 0.45) {
    const glint = (eye: EyePose, ox: number, oy: number, r: number): Primitive => {
      const c = facePoint(pose, eye.x + eye.pupilX + ox, eye.y + eye.pupilY + oy);
      return {
        type: "ellipse",
        cx: c.x,
        cy: c.y,
        rx: r * pose.scale,
        ry: r * pose.scale,
        fill: whites,
        stroke: "none",
        strokeWidth: 0,
      };
    };
    prims.push(glint(pose.eyeL, 0.5, -2.0, 0.9));
    prims.push(glint(pose.eyeR, 0.4, -2.4, 1.05));
  }
  return prims;
}

function crossEyePrimitives(pose: Pose, eye: EyePose, theme: DwimTheme, sw: number): Primitive[] {
  const s = 4.4;
  const a = facePoint(pose, eye.x - s, eye.y - s);
  const b = facePoint(pose, eye.x + s, eye.y + s);
  const c = facePoint(pose, eye.x + s, eye.y - s);
  const d = facePoint(pose, eye.x - s, eye.y + s);
  return [
    strokePath(`M ${a.x} ${a.y} L ${b.x} ${b.y}`, theme, sw * 1.15),
    strokePath(`M ${c.x} ${c.y} L ${d.x} ${d.y}`, theme, sw * 1.15),
  ];
}

function eyePrimitives(pose: Pose, eye: EyePose, theme: DwimTheme, sw: number): Primitive[] {
  if (eye.kind === "cross") {
    return crossEyePrimitives(pose, eye, theme, sw);
  }
  if (eye.kind === "dot") {
    if (eye.openness <= 0) return [];
    const r = 3.2 * Math.max(eye.openness, 0.15) * pose.scale;
    const c = facePoint(pose, eye.x + eye.pupilX, eye.y + eye.pupilY);
    return [{
      type: "ellipse",
      cx: c.x,
      cy: c.y,
      rx: r,
      ry: r,
      fill: theme.foreground,
      stroke: "none",
      strokeWidth: 0,
    }];
  }
  if (eye.kind === "arch") {
    return [strokePath(archEyePath(pose, eye), theme, sw)];
  }
  return [strokePath(closedLidPath(pose, eye), theme, sw)];
}

function facePrimitives(pose: Pose, theme: DwimTheme, sw: number): Primitive[] {
  const prims: Primitive[] = [];
  const crosses = pose.eyeL.kind === "cross" || pose.eyeR.kind === "cross";
  const closed = pose.eyeL.kind === "closed" || pose.eyeR.kind === "closed";
  if (crosses) {
    prims.push(...eyePrimitives(pose, pose.eyeL, theme, sw));
    prims.push(...eyePrimitives(pose, pose.eyeR, theme, sw));
  } else if (closed) {
    prims.push(...eyePrimitives(pose, pose.eyeL, theme, sw));
    prims.push(...eyePrimitives(pose, pose.eyeR, theme, sw));
  } else {
    prims.push(...goggleEyePrimitives(pose, theme, sw));
  }
  if (pose.brow > 0.05) {
    const outside = pose.browSide !== 0;
    const haloFill = isTransparentFill(theme.background) ? "#FFFFFF" : theme.background;
    const drawBrow = (eye: EyePose) => {
      const d = browPath(pose, eye, outside);
      if (outside) {
        prims.push({
          type: "path",
          d,
          fill: "none",
          stroke: haloFill,
          strokeWidth: sw * 4.2,
        });
      }
      prims.push(strokePath(d, theme, sw));
    };
    if (pose.browSide <= 0) drawBrow(pose.eyeL);
    if (pose.browSide >= 0) drawBrow(pose.eyeR);
  }
  prims.push(...mouthPrimitives(pose, theme, sw));
  return prims;
}

function roundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const cr = Math.min(r, w / 2, h / 2);
  return [
    `M ${x + cr} ${y}`,
    `H ${x + w - cr}`,
    `Q ${x + w} ${y} ${x + w} ${y + cr}`,
    `V ${y + h - cr}`,
    `Q ${x + w} ${y + h} ${x + w - cr} ${y + h}`,
    `H ${x + cr}`,
    `Q ${x} ${y + h} ${x} ${y + h - cr}`,
    `V ${y + cr}`,
    `Q ${x} ${y} ${x + cr} ${y}`,
    "Z",
  ].join(" ");
}

function rotundCloudPath(cx: number, cy: number, rx: number, ry: number): string {
  const n = 12;
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    const wobble = 1 + 0.07 * Math.sin(i * 2.7);
    pts.push({
      x: cx + rx * wobble * Math.cos(a),
      y: cy + ry * wobble * Math.sin(a),
    });
  }
  const br = ((rx + ry) / n) * 1.55;
  const parts = [`M ${pts[0].x} ${pts[0].y}`];
  for (let i = 0; i < n; i++) {
    const p = pts[(i + 1) % n];
    const r = br * (0.88 + 0.2 * (((i * 5) % 7) / 6));
    parts.push(`A ${r} ${r} 0 0 1 ${p.x} ${p.y}`);
  }
  parts.push("Z");
  return parts.join(" ");
}

const VIEWBOX = 240;
const VIEW_MARGIN = 8;
/** Left inset so reclined sleeping/out poses (and stroke) are not clipped. */
export const SCENE_VIEWBOX_MIN_X = -12;
export const SCENE_VIEWBOX = `${SCENE_VIEWBOX_MIN_X} 0 ${VIEWBOX - SCENE_VIEWBOX_MIN_X} ${VIEWBOX}`;

function captionNudgeX(pose: Pose): number {
  const { progress, kind } = pose.bubble;
  if (progress <= 0 || kind === "none") return 0;
  const recline = Math.min(1, Math.abs(pose.rotation) / 90);
  return -40 * progress * (1 - 0.45 * recline);
}

function withCaptionNudge(pose: Pose, dx: number): Pose {
  if (dx === 0) return pose;
  return {
    ...pose,
    x: pose.x + dx,
    tail1: { ...pose.tail1, x: pose.tail1.x + dx },
    tail2: { ...pose.tail2, x: pose.tail2.x + dx },
  };
}

function headRightExtent(pose: Pose): number {
  const corners: [number, number][] = [
    [HEAD_R, 0],
    [-HEAD_R, 0],
    [0, -HEAD_RY],
    [0, HEAD_RY],
  ];
  let maxX = pose.x;
  for (const [lx, ly] of corners) {
    const p = local(pose, lx * pose.scale, ly * pose.scale);
    if (p.x > maxX) maxX = p.x;
  }
  return maxX + 6;
}

function isReclinedErrorBubble(pose: Pose): boolean {
  return pose.rotation === 90 && pose.eyeL.kind === "cross";
}

function thinkBubbleTargetRadii(headRight: number): { rx: number; ry: number } {
  const room = VIEWBOX - VIEW_MARGIN - headRight - 8;
  const rx = Math.min(48, Math.max(22, room * 0.48));
  return { rx, ry: rx * 0.86 };
}

/** Usable text area inside the scalloped thought cloud (smaller than rx/ry). */
function thinkCaptionInner(rx: number, ry: number): { width: number; height: number } {
  return { width: rx * 1.35, height: ry * 1.3 };
}

const SPEAK_BUBBLE_W = 124;
const SPEAK_BUBBLE_H = 84;

function bubblePrimitives(pose: Pose, theme: DwimTheme, sw: number): Primitive[] {
  const { progress, kind, text } = pose.bubble;
  if (progress <= 0 || kind === "none") return [];
  const fill = resolveBubbleFill(theme, {
    fill: pose.bubble.fill,
    tone: pose.bubble.tone as CaptionOptions["tone"],
  });
  const headRight = headRightExtent(pose);
  const prims: Primitive[] = [];
  if (kind === "think") {
    const { rx: targetRx, ry: targetRy } = thinkBubbleTargetRadii(headRight);
    const rx = lerp(6, targetRx, progress);
    const ry = rx * 0.86;
    const cx = Math.min(headRight + 8 + rx, VIEWBOX - VIEW_MARGIN - rx - 10);
    const cy = Math.max(VIEW_MARGIN + ry + 10, pose.y - 48);
    prims.push({
      type: "path",
      d: rotundCloudPath(cx, cy, rx, ry),
      fill,
      stroke: theme.foreground,
      strokeWidth: sw,
    });
    if (progress > 0.45) {
      // Fit against the grown cloud, not the in-flight lerp size (avoids oversized captions).
      const inner = thinkCaptionInner(targetRx, targetRy);
      prims.push(
        ...centeredCaption(
          cx,
          cy,
          text,
          theme.foreground,
          inner.width,
          inner.height,
          BUBBLE_FONT_FAMILY,
          "compact",
        ),
      );
    }
    return prims;
  }
  const minSpeakW = isReclinedErrorBubble(pose) ? 109 : 56;
  const maxW = Math.max(minSpeakW, VIEWBOX - VIEW_MARGIN - headRight - 8);
  const targetW = Math.min(SPEAK_BUBBLE_W, maxW);
  const targetH = SPEAK_BUBBLE_H;
  let w = lerp(10, targetW, progress);
  const h = lerp(10, targetH, progress);
  let x = headRight + 8;
  if (x + w > VIEWBOX - VIEW_MARGIN) {
    x = VIEWBOX - VIEW_MARGIN - w;
  }
  x = Math.max(VIEW_MARGIN, x);
  w = Math.min(w, VIEWBOX - VIEW_MARGIN - x);
  if (isReclinedErrorBubble(pose)) {
    const minW = 109;
    if (w < minW) {
      x = Math.max(VIEW_MARGIN, VIEWBOX - VIEW_MARGIN - minW);
      w = Math.min(minW, VIEWBOX - VIEW_MARGIN - x);
    }
  }
  const y = Math.max(VIEW_MARGIN, Math.min(pose.y - 72, VIEWBOX - VIEW_MARGIN - h));
  prims.push({
    type: "path",
    d: roundedRectPath(x, y, w, h, 16),
    fill,
    stroke: theme.foreground,
    strokeWidth: sw,
  });
  if (progress > 0.45) {
    const { width: innerW, height: innerH } = speakCaptionInnerSize(w, h);
    const captionLayout = speakCaptionLayout(text, innerW, innerH);
    prims.push(
      ...centeredCaption(
        x + w / 2,
        y + h / 2,
        text,
        theme.foreground,
        innerW,
        innerH,
        BUBBLE_FONT_FAMILY,
        captionLayout,
        fitSpeakCaption(text, innerW, innerH, captionLayout),
      ),
    );
  }
  return prims;
}

function centeredCaption(
  cx: number,
  cy: number,
  text: string,
  fill: string,
  innerWidth: number,
  innerHeight: number,
  fontFamily = BUBBLE_FONT_FAMILY,
  layout: "standard" | "compact" = "standard",
  fit?: { lines: string[]; fontSize: number },
): Primitive[] {
  const { lines, fontSize } = fit ?? fitBubbleText(text, innerWidth, innerHeight, undefined, undefined, layout);
  if (lines.length === 0) return [];
  const lineHeight = fontSize * BUBBLE_LINE_HEIGHT;
  const blockH = (lines.length - 1) * lineHeight;
  const firstY = cy - blockH / 2 + fontSize * 0.32;
  return lines.map((line, i) => ({
    type: "text" as const,
    x: cx,
    y: firstY + i * lineHeight,
    text: line,
    fontSize,
    fill,
    anchor: "middle" as const,
    fontFamily,
  }));
}

/** Fixed-layout speak bubble for static raster export (splash), not live animation. */
export function exportSpeakBubblePrimitives(
  theme: DwimTheme,
  text: string,
  box: { x: number; y: number; w: number; h: number },
): Primitive[] {
  const sw = strokeForSize(240);
  const fill = resolveBubbleFill(theme, { tone: "default" });
  const { x, y, w, h } = box;
  const inner = speakCaptionInnerSize(w, h);
  return [
    {
      type: "path",
      d: roundedRectPath(x, y, w, h, 16),
      fill,
      stroke: theme.foreground,
      strokeWidth: sw,
    },
    ...centeredCaption(
      x + w / 2,
      y + h / 2,
      text,
      theme.foreground,
      inner.width,
      inner.height,
      BUBBLE_EXPORT_FONT_FAMILY,
    ),
  ];
}

export function poseToPrimitives(pose: Pose, theme: DwimTheme, pixelSize: number): Primitive[] {
  const sw = strokeForSize(pixelSize);
  const prims: Primitive[] = [];
  const body = withCaptionNudge(pose, captionNudgeX(pose));

  for (const paper of pose.papers) {
    prims.push({
      type: "rect",
      x: paper.x - paper.width / 2,
      y: paper.y - paper.height / 2,
      width: paper.width,
      height: paper.height,
      rotation: paper.rotation,
      fill: sheetFill(theme),
      stroke: theme.foreground,
      strokeWidth: sw,
    });
  }

  const scaleVec = (p: { x: number; y: number; rx: number; ry: number }) => ({
    cx: body.x + (p.x - body.x) * body.scale,
    cy: body.y + (p.y - body.y) * body.scale,
    rx: p.rx * body.scale,
    ry: p.ry * body.scale,
  });

  const t2 = scaleVec(body.tail2);
  const t1 = scaleVec(body.tail1);
  prims.push({
    type: "ellipse",
    cx: t2.cx,
    cy: t2.cy,
    rx: t2.rx,
    ry: t2.ry,
    fill: sheetFill(theme),
    stroke: theme.foreground,
    strokeWidth: sw,
  });
  prims.push({
    type: "ellipse",
    cx: t1.cx,
    cy: t1.cy,
    rx: t1.rx,
    ry: t1.ry,
    fill: sheetFill(theme),
    stroke: theme.foreground,
    strokeWidth: sw * 1.5,
  });

  prims.push(...headOutlinePrimitives(body, theme, pixelSize));

  prims.push(...facePrimitives(body, theme, sw));
  prims.push(...bubblePrimitives(body, theme, sw));
  return prims;
}
